import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { ExpiredSubscriptionCard } from "./inner/expiredSubscriptionCard.js";
import { SubscriptionPastDuePaymentReminder } from "./inner/subscriptionPastDuePaymentReminder.js";
import { SubscriptionCardHeader } from "./subscriptionCardHeader.js";
import { SubscriptionDetailBody } from "./subscriptionDetailBody.js";
import { useStores } from "../../../../../store/stores.js";
import { CancellationModal } from "../../modals/cancellationModal.js";
import { ChangeInvoiceAddressModal } from "../../modals/changeInvoiceAddressModal.js";
import { ChangePaymentDetailsModal } from "../../modals/changePaymentDetailsModal.js";
import { ChangePlanModal } from "../../modals/changePlanModal.js";
// Cancellation state dims the card visually; the only inline state cue is the
// status chip rendered inside the header.
const resolveStateClass = (subscription)=>{
    if (subscription.isCancelled) {
        return "rc-account-v2-subscription-card--cancelled";
    }
    return "rc-account-v2-subscription-card--active";
};
const SubscriptionCard = observer(({ subscription, defaultExpanded })=>{
    const { accountV2Store } = useStores();
    const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);
    const [isAnimating, setIsAnimating] = useState(false);
    const cardRef = useRef(null);
    const isFlashing = accountV2Store.flashSubscriptionId === subscription.data.id;
    useEffect(()=>{
        if (isFlashing && cardRef.current) {
            cardRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center"
            });
            // Start the flash animation after the smooth scroll completes
            // (~600 ms) so the pulse is visible on arrival, not during travel.
            const t = setTimeout(()=>setIsAnimating(true), 600);
            return ()=>clearTimeout(t);
        } else {
            setIsAnimating(false);
        }
        return undefined;
    }, [
        isFlashing
    ]);
    const openModal = accountV2Store.activeSubscriptionActionDialog;
    const isThisCardModal = openModal?.subscriptionId === subscription.data.id;
    const modalForThisCard = isThisCardModal ? openModal.kind : undefined;
    const autoSelectFirstUpgrade = isThisCardModal && openModal?.autoSelectFirstUpgrade === true;
    const className = [
        "rc-account-v2-subscription-card",
        resolveStateClass(subscription),
        isExpanded ? "rc-account-v2-subscription-card--expanded" : "",
        isAnimating ? "rc-account-v2-subscription-card--flash" : ""
    ].filter(Boolean).join(" ");
    return /*#__PURE__*/ _jsxs("article", {
        ref: cardRef,
        className: className,
        children: [
            /*#__PURE__*/ _jsx(SubscriptionCardHeader, {
                subscription: subscription,
                isExpanded: isExpanded,
                onToggle: ()=>setIsExpanded((v)=>!v)
            }),
            isExpanded && /*#__PURE__*/ _jsx("div", {
                className: "rc-account-v2-subscription-card__body",
                children: subscription.isExpired ? /*#__PURE__*/ _jsx(ExpiredSubscriptionCard, {
                    subscription: subscription
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        subscription.isPastDue && /*#__PURE__*/ _jsx(SubscriptionPastDuePaymentReminder, {
                            subscription: subscription
                        }),
                        /*#__PURE__*/ _jsx(SubscriptionDetailBody, {
                            subscription: subscription
                        })
                    ]
                })
            }),
            modalForThisCard === "change-plan" && /*#__PURE__*/ _jsx(ChangePlanModal, {
                subscription: subscription,
                autoSelectFirstUpgrade: autoSelectFirstUpgrade
            }),
            modalForThisCard === "change-payment" && /*#__PURE__*/ _jsx(ChangePaymentDetailsModal, {
                subscription: subscription
            }),
            modalForThisCard === "cancellation" && /*#__PURE__*/ _jsx(CancellationModal, {
                subscription: subscription
            }),
            modalForThisCard === "change-invoice-address" && /*#__PURE__*/ _jsx(ChangeInvoiceAddressModal, {
                subscription: subscription
            })
        ]
    });
});
export { SubscriptionCard };
